import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ShareNetwork, Plus, Pencil, Trash, ArrowsClockwise, X, CheckCircle, XCircle, Copy, ArrowsLeftRight, Receipt, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = () => ({ name: "", base_url: "", outbound_api_key: "", contact_email: "", notes: "", es_cliente: false });

export default function Partners() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/partners");
      setItems(r.data || []);
    } catch (e) {
      toast.error("No se pudieron cargar los partners");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSave = async (data) => {
    try {
      if (data.id) {
        await api.put(`/partners/${data.id}`, data);
        toast.success("Partner actualizado");
      } else {
        await api.post("/partners", data);
        toast.success("Partner creado");
      }
      setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`¿Eliminar partner "${p.name}"?`)) return;
    try {
      await api.delete(`/partners/${p.id}`); toast.success("Eliminado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onTest = async (p) => {
    const t = toast.loading(`Probando conexión con ${p.name}...`);
    try {
      const r = await api.post(`/partners/${p.id}/test-connection`);
      toast.dismiss(t);
      if (r.data?.ok) toast.success(`✓ Conexión OK con ${r.data?.remote?.you_are || p.name}`);
      else toast.error(`✗ Error: ${r.data?.error || "fallo desconocido"}`);
      load();
    } catch (e) { toast.dismiss(t); toast.error("Error de red al probar"); }
  };

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("Copiado al portapapeles");
  };

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight inline-flex items-center gap-2">
            <ShareNetwork className="w-6 h-6 text-brand" weight="duotone" />
            Gabinetes partner
          </h1>
          <p className="text-xs text-muted mt-1">
            Otros gabinetes con el mismo ERP con los que puedes <strong>compartir expedientes</strong> de forma segura.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setInviting(true)} data-testid="partner-invite" className="text-xs border border-border hover:border-foreground rounded-md px-3 py-2 inline-flex items-center gap-1.5 font-semibold bg-white">
            <EnvelopeSimple className="w-3.5 h-3.5" weight="bold" /> Invitar por email
          </button>
          <button onClick={() => setEditing(empty())} data-testid="partner-new" className="btn-brand inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md font-semibold">
            <Plus className="w-3.5 h-3.5" weight="bold" /> Añadir partner
          </button>
        </div>
      </div>

      <div className="card-elevated p-4 bg-brand-soft/30 border-brand/20 text-xs leading-relaxed">
        <strong className="text-foreground">¿Cómo funciona?</strong> Cada gabinete partner te entrega su <strong>URL del ERP</strong> y una <strong>API key</strong> que copias aquí.
        Por su parte, tú le entregas el token <strong>"Tu token"</strong> que aparece junto a cada partner para que él se conecte contra tu ERP.
        Una vez conectados, podrás compartir expedientes (datos, documentos, fotos, valoración…) desde la pantalla de cualquier expediente.
      </div>

      {/* Tabla */}
      <div className="card-elevated overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted">Cargando...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted">Sin partners. Añade el primero para empezar a compartir.</div>
        ) : (
          <table className="w-full text-sm" data-testid="partners-table">
            <thead className="thead-modern">
              <tr>
                <th>Gabinete</th>
                <th>URL</th>
                <th>Tu token (dáselo a ellos)</th>
                <th>Conexión</th>
                <th>Cliente</th>
                <th className="!text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p, idx) => (
                <tr key={p.id} className={`border-b border-[#F1F3F5] hover:bg-[#F5F6F8] transition ${idx % 2 ? "bg-[#FBFBFC]" : ""}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-medium">{p.name}</div>
                    {p.contact_email && <div className="text-xs text-muted">{p.contact_email}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-[12px] tabular-nums text-muted truncate max-w-[260px]">{p.base_url}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <code className="text-[10.5px] bg-[#FDF0DB] text-[#B45309] px-2 py-0.5 rounded border border-[#F5D9A6] truncate max-w-[160px]">{p.inbound_token}</code>
                      <button onClick={() => copy(p.inbound_token)} className="text-muted hover:text-foreground p-1" title="Copiar"><Copy className="w-3 h-3" weight="regular" /></button>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {p.last_ping_at ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                        <CheckCircle className="w-3 h-3" weight="fill" /> OK
                      </span>
                    ) : p.last_ping_error ? (
                      <span className="inline-flex items-center gap-1 text-rose-700 font-semibold" title={p.last_ping_error}>
                        <XCircle className="w-3 h-3" weight="fill" /> Error
                      </span>
                    ) : (
                      <span className="text-muted">No probado</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {p.es_cliente && p.cliente_facturacion_id ? (
                      <button
                        onClick={() => navigate(`/admin/clientes-facturacion?focus=${p.cliente_facturacion_id}`)}
                        className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                        data-testid={`partner-cli-${p.id}`}
                      >
                        <Receipt className="w-3 h-3" weight="bold" /> Ver ficha
                      </button>
                    ) : (
                      <span className="text-[10px] text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => onTest(p)} className="text-muted hover:text-brand p-1.5" title="Probar conexión" data-testid={`partner-test-${p.id}`}><ArrowsLeftRight className="w-3.5 h-3.5" weight="bold" /></button>
                    <button onClick={() => setEditing(p)} className="text-muted hover:text-brand p-1.5" title="Editar" data-testid={`partner-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" weight="bold" /></button>
                    <button onClick={() => onDelete(p)} className="text-muted hover:text-rose-600 p-1.5" title="Eliminar"><Trash className="w-3.5 h-3.5" weight="bold" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && <PartnerModal initial={editing} onClose={() => setEditing(null)} onSave={onSave} />}
      {inviting && <InviteModal onClose={() => setInviting(false)} />}
    </div>
  );
}

function InviteModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Indica un email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/partners/invite-by-email", { email: email.trim() });
      if (r.data?.ok) {
        toast.success(`Invitación enviada a ${email}`);
        onClose();
      } else if (r.data?.reason === "smtp-not-configured") {
        toast.error("SMTP no configurado. Pídele al administrador que rellene SMTP_PASSWORD en backend/.env.");
      } else {
        toast.error(r.data?.error || "No se pudo enviar la invitación");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error de red");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
        <div className="px-6 pt-5 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Invitar gabinete partner</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-[12px] leading-relaxed text-amber-900">
            Enviaremos un email al partner con tu URL y tu API Key pre-formateadas. Sólo tendrá que abrir su ERP, pegarlas y pulsar "Probar conexión". Cero pasos manuales.
          </div>
          <Field label="Email del responsable del gabinete partner *">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="invite-email" className="!text-xs" placeholder="responsable@otrogabinete.es" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-border bg-surface/30">
          <button type="button" onClick={onClose} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button type="submit" disabled={busy} data-testid="invite-send" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <EnvelopeSimple className="w-3 h-3" weight="bold" />
            {busy ? "Enviando..." : "Enviar invitación"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PartnerModal({ initial, onClose, onSave }) {
  const [data, setData] = useState(initial);
  const [probe, setProbe] = useState({ status: "idle", remote: null, error: null });
  const setF = (k, v) => {
    setData((p) => ({ ...p, [k]: v }));
    // Cualquier cambio en URL/key invalida el último probe
    if (k === "base_url" || k === "outbound_api_key") {
      setProbe({ status: "idle", remote: null, error: null });
    }
  };

  const test = async () => {
    if (!data.base_url || !data.outbound_api_key) {
      toast.error("Rellena URL y API Key antes de probar");
      return;
    }
    setProbe({ status: "loading", remote: null, error: null });
    try {
      const r = await api.post("/partners/test-credentials", {
        base_url: data.base_url,
        outbound_api_key: data.outbound_api_key,
      });
      if (r.data?.ok) {
        setProbe({ status: "ok", remote: r.data.remote, error: null });
        // Sugerir nombre si está vacío y el remoto nos identifica
        if (!data.name && r.data.remote?.you_are) {
          setF("name", r.data.remote.you_are);
        }
      } else {
        setProbe({ status: "error", remote: null, error: r.data?.error || "Error desconocido" });
      }
    } catch (e) {
      setProbe({ status: "error", remote: null, error: e?.response?.data?.detail || "Error de red" });
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!data.name || !data.base_url || !data.outbound_api_key) {
      toast.error("Nombre, URL y API key son obligatorios"); return;
    }
    onSave(data);
  };

  const canSave = data.name && data.base_url && data.outbound_api_key;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-5 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">{data.id ? "Editar partner" : "Conectar gabinete partner"}</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" weight="bold"/></button>
        </div>
        <div className="px-6 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {!data.id && (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-[12px] leading-relaxed text-amber-900">
              Pega la <strong>URL</strong> y la <strong>API Key</strong> que te ha dado el partner, y pulsa
              <strong> "Probar conexión"</strong>. Si todo está bien, podrás guardar y empezar a compartir expedientes.
            </div>
          )}
          <Field label="URL base del ERP del partner *">
            <input
              value={data.base_url}
              onChange={(e) => setF("base_url", e.target.value)}
              className="!text-xs font-mono"
              placeholder="https://tu-partner.preview.emergentagent.com"
              data-testid="partner-form-url"
            />
          </Field>
          <Field label="API Key del partner *">
            <input
              value={data.outbound_api_key}
              onChange={(e) => setF("outbound_api_key", e.target.value)}
              className="!text-xs font-mono"
              placeholder="dbf_..."
              data-testid="partner-form-key"
            />
          </Field>

          {/* Probe inline */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={test}
              disabled={!data.base_url || !data.outbound_api_key || probe.status === "loading"}
              data-testid="partner-form-probe"
              className="text-xs border border-border hover:border-foreground rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed bg-white font-semibold"
            >
              <ArrowsLeftRight className="w-3 h-3" weight="bold" />
              {probe.status === "loading" ? "Probando..." : "Probar conexión"}
            </button>
            {probe.status === "ok" && (
              <span className="text-xs text-emerald-700 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 font-semibold" data-testid="partner-probe-ok">
                <CheckCircle className="w-3 h-3" weight="fill" />
                Conexión OK · {probe.remote?.you_are || "partner identificado"}
              </span>
            )}
            {probe.status === "error" && (
              <span className="text-xs text-rose-700 inline-flex items-center gap-1.5 bg-rose-50 border border-rose-200 rounded-md px-3 py-1.5 font-semibold max-w-full" data-testid="partner-probe-error">
                <XCircle className="w-3 h-3 flex-shrink-0" weight="fill" />
                <span className="truncate">{probe.error}</span>
              </span>
            )}
          </div>

          <div className="border-t border-border pt-3">
            <Field label="Nombre del gabinete *">
              <input value={data.name} onChange={(e) => setF("name", e.target.value)} className="!text-xs" placeholder="Gefoex" data-testid="partner-form-name" />
            </Field>
            <Field label="Email de contacto">
              <input value={data.contact_email || ""} onChange={(e) => setF("contact_email", e.target.value)} className="!text-xs" />
            </Field>
            <Field label="Notas">
              <textarea value={data.notes || ""} onChange={(e) => setF("notes", e.target.value)} rows={2} className="!text-xs" />
            </Field>
            <div className="bg-amber-50/50 border border-amber-200 rounded-md px-3 py-2.5 mt-2">
              <label className="inline-flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!data.es_cliente}
                  onChange={(e) => setF("es_cliente", e.target.checked)}
                  className="rounded border-border mt-0.5"
                  data-testid="partner-es-cliente"
                />
                <div>
                  <div className="text-xs font-semibold">Es cliente</div>
                  <p className="text-[11px] text-muted leading-snug">Si lo marcas, se creará automáticamente una ficha en <strong>Clientes de facturación</strong> para poder emitirle facturas.</p>
                </div>
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-border bg-surface/30">
          <button type="button" onClick={onClose} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button
            type="submit"
            disabled={!canSave}
            data-testid="partner-form-save"
            className="btn-brand text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50"
          >
            {data.id ? "Guardar cambios" : (probe.status === "ok" ? "Guardar y conectar" : "Guardar")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-muted block mb-1">{label}</span>
      {children}
    </label>
  );
}
